"use client";
import LoginFormFooter from "@/components/authen/login/LoginFormFooter";
import FormItem from "@/components/common/Form/Item";
import InputPassword from "@/components/common/Input/Password";
import Link from "@/components/common/Typography/Link";
import Paragraph from "@/components/common/Typography/Paragraph";
import Text from "@/components/common/Typography/Text";
import Title from "@/components/common/Typography/Title";
import AppleFillIcon from "@/components/icons/AppleFillIcon";
import FacebookFillIcon from "@/components/icons/FacebookFillIcon";
import { useAppDispatch } from "@/lib/redux/hooks";
import { thunks } from "@/lib/redux/thunks";
import { Button, Col, Divider, Form, Input, message, Row } from "antd";
import Image from "next/image";
import { useState } from "react";
import ForgotPasswordModal, {
  ForgotPasswordModalRef,
} from "../forgotPassword/ForgotPasswordModal";
import { useRef } from "react";
import { toast } from "react-toastify";
import { useSearchParams } from "next/navigation";
import { useIsDesktop } from "@/hooks/app/useIsDesktop";
import { useTERouter } from "@/hooks/app/useTERouter";
import { useTranslation } from "react-i18next";
import { sanitizeReturnUrl } from "@/utils/auth/sanitizeReturnUrl";

const LoginForm = ({ onLoginSuccess, onSwitchToSignup }: any) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const forgotPasswordModalRef = useRef<ForgotPasswordModalRef>(null);
  const router = useTERouter();
  const searchParams = useSearchParams();
  const isDesktop = useIsDesktop();
  const { t } = useTranslation()

  const handleGoogleSignup = async () => {
    try {
      const googleOAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}&redirect_uri=${window.location.origin}/auth/google/callback&response_type=code&scope=email profile`;
      window.location.href = googleOAuthUrl;
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleFacebookSignup = async () => {
    try {
      const facebookUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${process.env.NEXT_PUBLIC_FACEBOOK_APP_ID}&redirect_uri=${window.location.origin}/auth/facebook/callback&response_type=code&scope=email`;
      window.location.href = facebookUrl;
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const response = await dispatch(
        thunks.login({
          body: {
            TYPE: "JSON",
            ...values,
          },
        })
      ).unwrap();

      toast.success(t("auth.signin_succeed"));
      form.resetFields();

      if (onLoginSuccess) {
        setLoading(false);
        onLoginSuccess(response);
        return;
      }

      const safeReturn = sanitizeReturnUrl(searchParams.get("returnUrl"));
      if (safeReturn) {
        setLoading(false);
        router.push(safeReturn);
        return;
      }

      if (!isDesktop && response.payload) {
        setLoading(false);
        router.push("/");
        return;
      }

      setLoading(false);
    } catch (error: any) {
      toast.error(t("auth.signin_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Form
        layout="vertical"
        className="form-login"
        form={form}
        onFinish={onFinish}
      >
        <Title level={2} style={{ textAlign: "center", marginTop: 0 }}>
          {t("common.login")}
        </Title>
        <Paragraph style={{ textAlign: "center" }}>
          {t("auth.login_message")}
        </Paragraph>
        <FormItem
          name="email"
          rules={[
            { required: true, message: t("auth.username_required_message") },
          ]}
        >
          <Input
            type="text"
            size="large"
            placeholder={t("auth.username_placeholder")}
          />
        </FormItem>
        <FormItem
          name="password"
          rules={[{ required: true, message: t("auth.password_required_message") }]}
        >
          <InputPassword size="large" placeholder={t("auth.password_placeholder")} />
        </FormItem>
        <Row justify="end">
          <Button
            className="form-login--btn-submit"
            size="large"
            type="link"
            onClick={() => forgotPasswordModalRef.current?.open()}
          >
            {t("auth.forgot_password")}
          </Button>
        </Row>
        <Row justify="end">
          <Button
            size="large"
            loading={loading}
            htmlType="submit"
            type="primary"
            block
          >
            {t("common.continue")}
          </Button>
        </Row>
        <Row className="form-login--divider-box">
          <Divider className="form-login--divider te-text-small">
            {t("auth.other_options_label")}
          </Divider>
        </Row>
        <Row justify="center" className="form-login--socialmedia">
          <Col className="form-login--socialmedia-item">
            <Button
              onClick={handleFacebookSignup}
              shape="circle"
              size="large"
              icon={<FacebookFillIcon />}
            />
          </Col>
          <Col className="form-login--socialmedia-item">
            <Button
              onClick={handleGoogleSignup}
              shape="circle"
              size="large"
              icon={
                <Image
                  alt="Google logo"
                  src={"/static/google-logo.png"}
                  height={48}
                  width={48}
                />
              }
            />
          </Col>
          {/* <Col className="form-login--socialmedia-item">
            <Button shape="circle" size="large" icon={<AppleFillIcon />} />
          </Col> */}
        </Row>
        <Row justify="center" className="form-login--policy">
          <Text>
            {t("auth.term_and_condition_1")}{" "}
            <Link href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">{t("home.policy_regulation")}</Link> {t("common.and")}{" "}
            <Link href="/chinh-sach-bao-mat" target="_blank" rel="noopener noreferrer">{t("home.privacy_policy")}</Link> {t("auth.term_and_condition_2", { platform: process.env.NEXT_PUBLIC_PLATFORM_NAME || "" })}.
          </Text>
        </Row>
        <LoginFormFooter onSwitchToSignup={onSwitchToSignup} />
      </Form>
      <ForgotPasswordModal ref={forgotPasswordModalRef} />
    </>
  );
};

export default LoginForm;
